import { type FieldError, type Path, type UseFormRegister } from 'react-hook-form'

import { useGetCensuses } from '../hooks/useGetCensuses'
import { FormSelectField } from '../reusable/form/FormFields'
import { DEFAULT_PAGE_SIZE } from '../reusable/table/TanstackTable'

export function CensusDropdown<T extends { quoting_prospect_employee_census_id: string }> ({
  prospectId,
  disabled,
  error,
  register,
}: {
  prospectId: string;
  disabled?: boolean;
  error?: FieldError;
  register: UseFormRegister<T>;
}) {
  // We are making an assumption that we always show the first 20 censuses available for the prospect
  // (which has been a behavior since Sept 2024) and we don't have any cases where there are more than
  // 20 censuses available for a prospect.
  const {
    data,
    error: apiError,
    isLoading,
  } = useGetCensuses({
    prospectId,
    pagination: {
      pageIndex: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    statusFilter: 'processed',
  })

  if (!isLoading && apiError) {
    return (
      <p className='mt-6 text-ds-text-info-critical-default text-xs'>
        There was a problem loading the existing censuses. If you want to use
        existing censuses please try later or try uploading a new census
        instead.
      </p>
    )
  }

  if (isLoading || !(data?.data?.length ?? 0)) {
    return null
  }

  const censusOptions = data!.data.map((census) => ({
    label: census.file_name,
    value: census.id,
  }))

  return (
    <>
      <div className='flex items-center my-4'>
        <div className='flex-grow border-t border-ds-primary-default' />
        <span className='mx-4 text-xs text-ds-text-secondary'>or</span>
        <div className='flex-grow border-t border-ds-primary-default' />
      </div>
      <FormSelectField<T>
        fieldId={'quoting_prospect_employee_census_id' as Path<T>}
        fieldLabel='Use previously uploaded file'
        fieldOptions={censusOptions}
        register={register}
        error={error}
        disabled={disabled}
      />
    </>
  )
}
